import * as React from 'react';

import twinkleSparkleSound from '@/assets/music/twinklesparkle.mp3';
import { useProfilesData } from '@/features/user/api';
import { useIdleTimer } from '@/hooks/use-idle-timer';

import { useBhanzuBuddy } from '../api/queries';
import { API_CONSTANTS, TIME_CONSTANTS } from '../constants';
import {
  checkAndUpdateLoginTriggers,
  getRandomChallengeQuestionWithMeta,
  hasShownOfflineQuickChallengeToday,
  markOfflineQuickChallengeShownToday,
} from '../utils/quick-challenge-questions/quick-challenge-utils';

interface UseQuickChallengeProps {
  isOnDashboard: boolean;
  hasUserInteracted: boolean;
  isOfflineDelivery: boolean;
  showQuickChallenge: { open: boolean; question: string };
  quickChallengeSnoozeUntil: number;
  chatState: 'closed' | 'opening' | 'open' | 'closing';
  isOpen: boolean;
  setShowQuickChallenge: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      question: string;
      category: string;
      color: string;
    }>
  >;
  quickChallengeContextRef: React.RefObject<{
    questionByBB: string;
  } | null>;
  leadId?: string;
  enabled?: boolean;
}

export const useQuickChallenge = ({
  isOnDashboard,
  hasUserInteracted,
  isOfflineDelivery,
  showQuickChallenge,
  quickChallengeSnoozeUntil,
  chatState,
  isOpen,
  setShowQuickChallenge,
  quickChallengeContextRef,
  leadId,
  enabled = true,
}: UseQuickChallengeProps) => {
  const { selectedProfile } = useProfilesData();
  const { useGetChatHistory } = useBhanzuBuddy();
  const chatHistoryQuery = useGetChatHistory({
    params: {
      lead_id: leadId ?? API_CONSTANTS.DUMMY_LEAD_ID,
      source: API_CONSTANTS.DEFAULT_SOURCE,
    },
    // Interaction history is fetched on open via BhanzuBuddy effect
    enabled: false,
  });

  const playTwinkleSound = React.useCallback(() => {
    try {
      const audio = new Audio(twinkleSparkleSound);
      audio.volume = 0.6;
      void audio.play();
    } catch {
      // empty catch block
    }
  }, []);

  const { reset: resetIdleTimer } = useIdleTimer({
    timeout: TIME_CONSTANTS.IDLE_TIMEOUT,
    onIdle: () => {
      if (
        enabled &&
        isOnDashboard &&
        hasUserInteracted &&
        !isOfflineDelivery &&
        (!showQuickChallenge.open || !showQuickChallenge.question) &&
        Date.now() > quickChallengeSnoozeUntil &&
        chatState === 'closed' &&
        !isOpen
      ) {
        const questionData = getRandomChallengeQuestionWithMeta(
          selectedProfile?.grade,
          chatHistoryQuery.data?.interactions
        );
        setShowQuickChallenge({
          open: true,
          question: questionData.text,
          category: questionData.category,
          color: questionData.color,
        });
        quickChallengeContextRef.current = {
          questionByBB: questionData.text,
        };
      }
    },
  });

  React.useEffect(() => {
    if (!enabled) {
      return () => {};
    }

    const checkLoginTriggers = () => {
      if (isOfflineDelivery) {
        if (
          !hasShownOfflineQuickChallengeToday() &&
          (!showQuickChallenge.open || !showQuickChallenge.question) &&
          chatState === 'closed' &&
          !isOpen
        ) {
          const questionData = getRandomChallengeQuestionWithMeta(
            selectedProfile?.grade,
            chatHistoryQuery.data?.interactions
          );
          setShowQuickChallenge({
            open: true,
            question: questionData.text,
            category: questionData.category,
            color: questionData.color,
          });
          quickChallengeContextRef.current = {
            questionByBB: questionData.text,
          };
          markOfflineQuickChallengeShownToday();
        }
      } else {
        const { shouldShow } = checkAndUpdateLoginTriggers();
        if (
          shouldShow &&
          (!showQuickChallenge.open || !showQuickChallenge.question) &&
          chatState === 'closed' &&
          !isOpen
        ) {
          const questionData = getRandomChallengeQuestionWithMeta(
            selectedProfile?.grade,
            chatHistoryQuery.data?.interactions
          );
          setShowQuickChallenge({
            open: true,
            question: questionData.text,
            category: questionData.category,
            color: questionData.color,
          });
          quickChallengeContextRef.current = {
            questionByBB: questionData.text,
          };
        }
      }
    };
    checkLoginTriggers();
    if (!isOfflineDelivery) {
      const interval = setInterval(checkLoginTriggers, 3_600_000);
      return () => clearInterval(interval);
    }
    return () => {};
  }, [
    enabled,
    showQuickChallenge.open,
    showQuickChallenge.question,
    chatState,
    isOpen,
    isOfflineDelivery,
    selectedProfile?.grade,
    chatHistoryQuery.data?.interactions,
    quickChallengeContextRef,
    setShowQuickChallenge,
  ]);

  React.useEffect(() => {
    if (showQuickChallenge.open && showQuickChallenge.question) {
      playTwinkleSound();
    }
  }, [showQuickChallenge.open, showQuickChallenge.question, playTwinkleSound]);

  return { resetIdleTimer, chatHistoryQuery };
};
